import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config';
import { authenticatedFetch } from '../utils/api';
import { useProfile } from './ProfileContext';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * ThemeProvider - Uses ProfileContext to eliminate redundant /api/profile/user call
 * 
 * Optimization: Instead of fetching /api/profile/user just for darkMode,
 * we use the user data from ProfileContext which already fetches /api/profile
 * containing the same user information.
 * 
 * Note: This component must be used inside ProfileProvider to access useProfile()
 */
export const ThemeProvider = ({ children }) => {
  // Get user data from ProfileContext (eliminates redundant API call)
  const { user } = useProfile();
  
  // Initialize from localStorage immediately for instant UI update
  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem('darkMode');
    if (stored !== null) {
      return stored === 'true';
    }
    // Fallback to system preference if no localStorage value
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  // Apply theme to DOM immediately on mount and whenever isDark changes
  useEffect(() => {
    // Set data-theme attribute for CSS variables
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    
    // Also set classes for backward compatibility
    if (isDark) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }
    
    // Save to localStorage immediately
    localStorage.setItem('darkMode', isDark.toString());
  }, [isDark]);

  // Sync theme from ProfileContext when profile data is available (eliminates redundant API call)
  useEffect(() => {
    if (user?.darkMode !== undefined) {
      const apiDarkMode = user.darkMode === 1;
      const localDarkMode = localStorage.getItem('darkMode') === 'true';
      
      // Only update if API value differs from localStorage (sync them)
      if (apiDarkMode !== localDarkMode) {
        setIsDark(apiDarkMode);
        localStorage.setItem('darkMode', apiDarkMode.toString());
      }
    }
  }, [user?.darkMode]);

  const toggleDarkMode = async () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    localStorage.setItem('darkMode', newIsDark.toString());

    // Sync with API only if user is logged in (non-blocking)
    if (user) {
      try {
        await authenticatedFetch(API_ENDPOINTS.PROFILE_USER, {
          method: 'PUT',
          body: JSON.stringify({
            darkMode: newIsDark ? 1 : 0,
          }),
        });
      } catch (error) {
        // Only log error if it's not a redirect (401)
        if (!error.redirected) {
          console.error('Error updating theme in database:', error);
        }
        // Theme is still saved in localStorage, so it will persist
      }
    }
    // For unauthenticated users, theme is only saved in localStorage
  };

  // Alias for backward compatibility
  const toggleTheme = toggleDarkMode;

  const value = {
    isDark,
    toggleDarkMode,
    toggleTheme, // Alias for backward compatibility
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

